/**
 * DM-8485: the outreach timeline behind one lead and, for operators, the
 * form that appends to it. Rendered in the lead drawer after Account &
 * contact. Auditors see the timeline only.
 */

import { useState } from 'react';
import { useAuth } from '../../components/AuthProvider';
import type {
  WebsiteLeadOutreachEventType,
  WebsiteLeadOutreachSummary,
  WebsiteLeadType,
} from '../../api/adminOps';
import { StatusChip } from '../../components/ui';
import { useLogWebsiteLeadOutreach, useWebsiteLeadOutreach } from '../../hooks/useAdminOps';
import {
  OUTREACH_ACTOR_MAX_LENGTH,
  OUTREACH_EVENT_TYPES,
  OUTREACH_NOTE_MAX_LENGTH,
  OUTREACH_SUBJECT_MAX_LENGTH,
  OUTREACH_TOUCHES,
  canHaveTouch,
  eventHint,
  eventLabel,
  fromLocalDateTimeInput,
  outreachChip,
  outreachSummaryLine,
  toLocalDateTimeInput,
} from '../websiteLeads.logic';
import { DetailSection, absolute, relative } from './parts';

interface OutreachLead {
  id: string;
  outreach: WebsiteLeadOutreachSummary;
}

export default function OutreachSection({
  leadType,
  lead,
}: {
  leadType: WebsiteLeadType;
  lead: OutreachLead;
}) {
  // LOG_WEBSITE_LEAD_OUTREACH is a write: auditors read the timeline only.
  const { hasRole } = useAuth();
  const canWrite = hasRole('admin', 'admin-operator');
  const log = useWebsiteLeadOutreach(leadType, lead.id);
  // The log's summary is fresher than the list row's once an event is logged.
  const summary = log.data?.meta.summary ?? lead.outreach;
  const chip = outreachChip(summary);
  const line = outreachSummaryLine(summary);

  return (
    <DetailSection title="Outreach">
      <div className="space-y-1">
        <StatusChip label={chip.label} tone={chip.tone} />
        {line && <p className="text-xs text-gray-text">{line}</p>}
      </div>

      {log.isPending && <p className="text-sm text-gray-text">Loading outreach…</p>}
      {log.isError && (
        <div className="text-sm">
          <p className="text-red-400">
            {log.error instanceof Error ? log.error.message : 'Could not load the outreach log'}
          </p>
          <button onClick={() => log.refetch()} className="btn-secondary mt-2">
            Retry
          </button>
        </div>
      )}
      {log.isSuccess &&
        (log.data.data.length === 0 ? (
          <p className="text-sm text-gray-text">Nothing logged yet.</p>
        ) : (
          <ol className="space-y-2" aria-label="Outreach timeline">
            {log.data.data.map(event => (
              <li key={event.id} className="text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-white">
                    {eventLabel(event.event_type)}
                    {event.touch !== null && (
                      <span className="text-gray-text"> · touch {event.touch}</span>
                    )}
                  </span>
                  <span
                    className="text-gray-text whitespace-nowrap"
                    title={absolute(event.occurred_at) ?? undefined}
                  >
                    {relative(event.occurred_at)}
                  </span>
                </div>
                {event.subject && (
                  <p className="text-xs text-gray-text break-words">“{event.subject}”</p>
                )}
                {event.note && (
                  <p className="text-xs text-white/80 whitespace-pre-wrap break-words">
                    {event.note}
                  </p>
                )}
                <p className="text-xs text-gray-text/70">{event.actor_label ?? event.actor_id}</p>
              </li>
            ))}
          </ol>
        ))}

      {canWrite && <OutreachLogForm key={lead.id} leadType={leadType} leadId={lead.id} />}
    </DetailSection>
  );
}

/** Compact form appending one event; the touch only applies to a sent email. */
function OutreachLogForm({ leadType, leadId }: { leadType: WebsiteLeadType; leadId: string }) {
  const { user } = useAuth();
  const logMutation = useLogWebsiteLeadOutreach();
  const [eventType, setEventType] = useState<WebsiteLeadOutreachEventType>('email_sent');
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput(new Date()));
  const [touch, setTouch] = useState('');
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');
  const [actorLabel, setActorLabel] = useState(user?.email ?? '');
  const occurredAtIso = fromLocalDateTimeInput(occurredAt);
  const hint = eventHint(eventType);
  const showTouch = canHaveTouch(eventType);

  const save = () => {
    if (occurredAtIso === null) return;
    const trimmedSubject = subject.trim();
    const trimmedNote = note.trim();
    const trimmedActor = actorLabel.trim();
    logMutation.mutate(
      {
        id: leadId,
        body: {
          lead_type: leadType,
          event_type: eventType,
          occurred_at: occurredAtIso,
          ...(showTouch && touch !== '' ? { touch: Number(touch) } : {}),
          ...(trimmedSubject ? { subject: trimmedSubject } : {}),
          ...(trimmedNote ? { note: trimmedNote } : {}),
          ...(trimmedActor ? { actor_label: trimmedActor } : {}),
        },
      },
      {
        onSuccess: () => {
          setSubject('');
          setNote('');
          setTouch('');
        },
      }
    );
  };

  return (
    <div className="rounded-lg border border-gray-border p-3 space-y-2">
      <p className="text-sm font-medium text-white">Log outreach</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="label" htmlFor="outreach-event">
            Event
          </label>
          <select
            id="outreach-event"
            className="input"
            value={eventType}
            onChange={event => {
              const next = OUTREACH_EVENT_TYPES.find(type => type === event.target.value);
              if (next) setEventType(next);
            }}
          >
            {OUTREACH_EVENT_TYPES.map(type => (
              <option key={type} value={type}>
                {eventLabel(type)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="outreach-when">
            When
          </label>
          <input
            id="outreach-when"
            type="datetime-local"
            className="input"
            value={occurredAt}
            onChange={event => setOccurredAt(event.target.value)}
          />
        </div>
        {showTouch && (
          <div>
            <label className="label" htmlFor="outreach-touch">
              Touch
            </label>
            <select
              id="outreach-touch"
              className="input"
              value={touch}
              onChange={event => setTouch(event.target.value)}
            >
              <option value="">—</option>
              {OUTREACH_TOUCHES.map(option => (
                <option key={option} value={String(option)}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={showTouch ? '' : 'col-span-2'}>
          <label className="label" htmlFor="outreach-actor">
            Logged for
          </label>
          <input
            id="outreach-actor"
            className="input"
            maxLength={OUTREACH_ACTOR_MAX_LENGTH}
            placeholder="Who did this"
            value={actorLabel}
            onChange={event => setActorLabel(event.target.value)}
          />
        </div>
      </div>
      <div>
        <label className="label" htmlFor="outreach-subject">
          Subject
        </label>
        <input
          id="outreach-subject"
          className="input"
          maxLength={OUTREACH_SUBJECT_MAX_LENGTH}
          placeholder="Email subject, if any"
          value={subject}
          onChange={event => setSubject(event.target.value)}
        />
      </div>
      <div>
        <label className="label" htmlFor="outreach-note">
          Note
        </label>
        <textarea
          id="outreach-note"
          className="input w-full min-h-[60px]"
          maxLength={OUTREACH_NOTE_MAX_LENGTH}
          value={note}
          onChange={event => setNote(event.target.value)}
        />
      </div>
      {hint && <p className="text-xs text-gray-text">{hint}</p>}
      <button
        onClick={save}
        disabled={logMutation.isPending || occurredAtIso === null}
        className="btn-primary w-full disabled:opacity-50"
      >
        {logMutation.isPending ? 'Saving…' : 'Log event'}
      </button>
    </div>
  );
}
